import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { DialogFooter } from '@/components/ui/dialog'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridFilterChangeEvent,
  GridHeaderSelectionChangeEvent,
  GridPageChangeEvent,
  GridSelectionChangeEvent,
} from '@progress/kendo-react-grid'
import { Pagination } from 'api/Pagination/Types/Pagination'
import { normalizeFilter } from 'features/DataGrid'
import uniqBy from 'lodash/uniqBy'
import { GridFilterActionState } from 'store/grid'

import { Item, SelectOverviewTableProps } from './types'

export const SelectDataFetchOverviewTable = <T extends object>({
  items,
  columns,
  onSingleSelect,
  onMultipleSelect,
  selectText,
  filterState,
  onGridPageChange,
  onFilterChange,
}: SelectOverviewTableProps<T> & {
  filterState: GridFilterActionState
  onGridPageChange: (gridFilterStateEvent: GridFilterActionState) => void
  onFilterChange: (filterDescriptor: GridFilterActionState) => void
}): JSX.Element => {
  const [dataSelected, setDataSelected] = useState<Item<T>[]>([])

  const handleGridPageChange = (event: GridPageChangeEvent) => {
    const { skip, take } = event.page
    const pagination = new Pagination({
      skip,
      recordsPerPage: take,
    })
    const pageIndex = !event.nativeEvent ? 1 : pagination.getPageIndex()
    const pageSize = !event.nativeEvent ? take : pagination.getRecordsPerPage()

    onGridPageChange({
      ...filterState,
      take,
      skip,
      pageIndex,
      pageSize,
      total: filterState.total,
    })
  }

  const handleMultipleSelect = (usersSelected: Item<T>[]) => {
    const itemsSelected = uniqBy(usersSelected, 'id')
    if (onMultipleSelect) {
      return onMultipleSelect(itemsSelected)
    }
  }

  const handleSingleSelect = (userSelected: Item<T>) => {
    if (onSingleSelect) {
      onSingleSelect(userSelected)
    }
  }

  const handleHeaderSelectionChange = (event: GridHeaderSelectionChangeEvent<HTMLInputElement>) => {
    const isAllSelected = event.dataItems.every((x) => !!x.selected)
    const itemsSelected = isAllSelected
      ? dataSelected.filter((selectedItem) => {
          const hasItem = items.find((item) => item.id === selectedItem.id)
          return !hasItem
        })
      : uniqBy([...items, ...dataSelected], 'id')

    setDataSelected(itemsSelected)
  }

  const handleSelectionChange = (event: GridSelectionChangeEvent) => {
    const user = items.find((x: Item<T>) => x.id === event.dataItem.id)
    if (user) {
      const selectedUser = dataSelected.find((x: Item<T>) => x.id === user.id)
      if (selectedUser) {
        setDataSelected(dataSelected.filter((x: Item<T>) => x.id !== user.id))
      } else {
        setDataSelected([...dataSelected, user])
      }
    }
  }

  const handleFilterChange = async (event: GridFilterChangeEvent) => {
    onFilterChange({
      ...filterState,
      pageIndex: 1,
      skip: 0,
      filter: {
        ...filterState.filter,
        ...normalizeFilter(event.filter),
      },
    })
  }

  const headerSelectionValue = (): boolean => {
    if (dataSelected.length <= 0) {
      return false
    }

    return selectItems().every((x) => x.selected)
  }

  const selectItems = () => {
    if (dataSelected.length <= 0) {
      return items
    }

    return items.map((item) => {
      if (dataSelected.length <= 0) {
        return { ...item, selected: false }
      }

      const match = dataSelected.find((x) => x.id === item.id)
      const isSelected = !!match

      return { ...item, selected: isSelected }
    })
  }

  return (
    <>
      <Grid
        filterable
        pageable
        resizable
        selectedField='selected'
        scrollable='scrollable'
        data={selectItems()}
        skip={filterState.skip}
        take={filterState.take}
        total={filterState.total}
        filter={filterState.filter}
        onHeaderSelectionChange={handleHeaderSelectionChange}
        onSelectionChange={handleSelectionChange}
        onFilterChange={handleFilterChange}
        onPageChange={handleGridPageChange}
      >
        {onSingleSelect && selectText && (
          <Column
            field='Select'
            width={130}
            filterable={false}
            headerCell={undefined}
            cell={(props: GridCellProps) => (
              <td>
                <Button variant='outline' onClick={() => handleSingleSelect(props.dataItem)}>
                  {selectText}
                </Button>
              </td>
            )}
          />
        )}
        {onMultipleSelect && (
          <Column
            field='selected'
            width='50px'
            filterable={false}
            headerCell={undefined}
            headerSelectionValue={headerSelectionValue()}
          />
        )}
        {columns.map((column, idx) => (
          <Column
            key={idx}
            field={column.field}
            title={column.title}
            className={column.className}
            cell={column.cell}
            filterCell={column.filterCell}
          />
        ))}
      </Grid>
      {onMultipleSelect && (
        <DialogFooter>
          <Button onClick={() => handleMultipleSelect(dataSelected)}>Save</Button>
        </DialogFooter>
      )}
    </>
  )
}
